import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'

import { reatomUpdateLogoDrawer } from '../../atoms'

import { PremiumImageDrawerPreview } from './update-logo-drawer-preview'
import { UpdateLogoProgress } from './update-logo-progress'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
  open: boolean
  onClose: () => void
}

export const UploadPremiumLogoContent: React.FC<Props> = ({
  pair,
  open,
  onClose,
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUpdateLogoDrawer({
        premiumMeta: pair.premiumInfo,
      }),
    [pair.premiumInfo],
  )
  const [requestState] = useAtom(model.updateRequestState)
  const [preview] = useAtom(model.previewAtom)
  const [image] = useAtom(model.imageAtom)
  const [currentIcon] = useAtom(pair.quote.icon)

  const uploadLogo = useAction(model.updateLogoAction)
  const setImage = useAction(model.setImageAction)
  const clearImage = useAction(model.clearImageAction)
  const reset = useAction(model.resetAction)

  const handleUpload = () =>
    ref.current?.dispatchEvent(new MouseEvent('click', { bubbles: true }))

  const clearFileInput = () => {
    if (ref.current) {
      ref.current.value = ''
    }
  }
  const handleReset = () => {
    reset()
    clearFileInput()
  }
  const handleClose = () => {
    handleReset()
    onClose()
  }
  const handleRevertImage = () => {
    clearImage()
    clearFileInput()
  }

  useEffect(() => {
    if (!open) {
      handleReset()
    }
    // Reset only when the drawer closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Root>
      <UpdateLogoProgress
        requestState={requestState}
        onClose={handleClose}
        onReset={handleReset}
      />

      <PremiumImageDrawerPreview
        onUploadClick={handleUpload}
        onRevertClick={preview ? handleRevertImage : undefined}
        imagePreview={preview || currentIcon}
        pair={pair}
      />
      <Input
        ref={ref}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            setImage(file)
          }
        }}
      />

      <Action>
        <ButtonBack
          radius="rounded"
          variant="secondary"
          type="button"
          size="m"
          onClick={handleClose}
        >
          {t('common.back')}
        </ButtonBack>
        <Button
          type="button"
          radius="rounded"
          variant="solid"
          color="primary"
          size="m"
          disabled={!image}
          onClick={uploadLogo}
        >
          {t('common.process')}
        </Button>
      </Action>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  text-align: center;
`

const Action = styled.footer`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 10px;
  padding: 42px 12px 34px;
`

const ButtonBack = styled(Button)`
  color: var(--text-11);
`

const Input = styled.input`
  display: none;
`
